import React from 'react';
import { GraphQLError } from 'graphql';
import { ApolloError } from '@apollo/client';
import { css } from '@emotion/react';
import tw from 'twin.macro';

interface ErrorMessageProps {
  error?: string | ApolloError;
  errors?: GraphQLError[];
  className?: string;
}

export const networkErrorMessage = `An error has occurred. Please try again.`;

export const cleanupError = (e?: string): string | undefined => {
  if (!e) return undefined;

  const networkMatches = e.match(/\s*Network error:\s*(.*)/);
  if (networkMatches && networkMatches.length > 1) {
    return networkErrorMessage;
  }

  const matches = e.match(/GraphQL error:\s*(.*)$/);
  if (matches && matches.length > 1) {
    e = matches[1];
  }
  return e;
};

export const ErrorMessage: React.FC<ErrorMessageProps> = ({ error, errors, className }) => {
  if (!error && (!errors || errors.length === 0)) {
    return null;
  }

  let e = error?.toString() ?? errors![0].message;
  if (e) {
    e = cleanupError(e)!;
  }

  return (
    <div
      css={css`
        martin-top: 1rem;
        ${tw`w-full flex justify-center text-color-error`}
      `}
      className={className}
    >
      {e}
    </div>
  );
};

export const TextMessage: React.FC<{ message: string | null }> = ({ message }) => {
  if (!message || message === '') {
    return null;
  }
  return (
    <div className='w-full flex justify-center'>
      <span className='w-full text-color-success'>{message}</span>
    </div>
  );
};

export const StatusMessage: React.FC<{
  message?: string;
  errors?: GraphQLError[];
  error?: string | ApolloError;
  hideCircles?: boolean;
  className?: string;
}> = ({ message, error, errors, className }) => {
  return (
    // ⚬⚬⚬ keeps the line-height exactly right.  Could replace with min-height I suppose.

    <div
      css={css`
        margin-top: 1rem;
      `}
      className={`w-full flex text-center justify-center ${className}`}
    >
      {error || (errors && errors!.length > 0) ? (
        <ErrorMessage errors={errors} error={error} className={className} />
      ) : (
        <span
          className='w-full text-color-success'
          css={css`
            height: 1.25rem;
            min-height: 1.25rem;
          `}
        >
          {message ?? ''}
        </span>
      )}
    </div>
  );
};

export default { TextMessage, ErrorMessage };
